import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Unlink, QrCode, Paperclip } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const MEDIA_LABELS = ["📷 Photo", "🎬 Video", "🎧 Audio", "💟 Sticker"];

function WAMedia({ jid, m }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["wa-media", jid, m.id],
    queryFn: () => api.get("/whatsapp/media", { params: { jid, id: m.id } }).then((r) => r.data),
    staleTime: Infinity,
    retry: 1,
  });
  if (isLoading) return <div className="my-1 h-24 w-36 animate-pulse rounded-lg bg-black/10 dark:bg-white/10" data-testid="wa-media-loading" />;
  if (isError || !data?.data) return <span className="text-xs opacity-70">{m.text} (unavailable)</span>;
  if (m.mediaType === "audio") return <audio controls src={data.data} className="my-1 h-9 w-52 max-w-full" data-testid="wa-audio" />;
  if (m.mediaType === "video") return <video controls src={data.data} className="my-1 max-h-40 rounded-lg" data-testid="wa-video" />;
  return <img src={data.data} alt="media" className="my-1 max-h-40 rounded-lg" data-testid="wa-image" />;
}

export const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 448 512" fill="currentColor" {...props}>
    <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
  </svg>
);

export default function WhatsAppCard({ card }) {
  const qc = useQueryClient();
  const [jid, setJid] = useState("");
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [connectRequested, setConnectRequested] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newChatInput, setNewChatInput] = useState("");
  const [pendingChat, setPendingChat] = useState(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  const { data: status } = useQuery({
    queryKey: ["wa-status"],
    queryFn: () => api.get("/whatsapp/status").then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.connected ? 12000 : 2500),
  });

  const connected = !!status?.connected;

  useEffect(() => {
    if (status?.qr || status?.connected) setConnectRequested(false);
  }, [status?.qr, status?.connected]);

  const { data: chats = [] } = useQuery({
    queryKey: ["wa-chats"],
    queryFn: () => api.get("/whatsapp/chats").then((r) => r.data),
    enabled: connected,
    refetchInterval: 5000,
  });

  const activeJid = jid || chats[0]?.jid || "";

  const { data: contacts = [] } = useQuery({
    queryKey: ["wa-contacts"],
    queryFn: () => api.get("/whatsapp/contacts").then((r) => r.data),
    enabled: connected && newChatOpen,
    staleTime: 60000,
  });

  const startChat = async () => {
    const input = newChatInput.trim();
    if (!input) return;
    const byName = contacts.find((c) => c.name.toLowerCase() === input.toLowerCase());
    try {
      let target;
      if (byName) target = { jid: byName.jid, name: byName.name };
      else {
        const digits = input.replace(/\D/g, "");
        if (digits.length < 8) { toast.error("Pick a contact or type the full number with country code (e.g. 9198XXXXXXXX)"); return; }
        const { data } = await api.post("/whatsapp/check", { number: digits });
        if (!data.exists || !data.jid) { toast.error("This number is not on WhatsApp"); return; }
        target = { jid: data.jid, name: `+${digits}` };
      }
      setPendingChat(target);
      setJid(target.jid);
      setNewChatOpen(false);
      setNewChatInput("");
    } catch (e) { toast.error(apiError(e)); }
  };

  const { data: messages = [] } = useQuery({
    queryKey: ["wa-msgs", activeJid],
    queryFn: () => api.get("/whatsapp/messages", { params: { jid: activeJid } }).then((r) => r.data),
    enabled: connected && !!activeJid,
    refetchInterval: 3000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, activeJid]);

  const connect = async () => {
    setConnectRequested(true);
    try {
      await api.post("/whatsapp/connect");
      qc.invalidateQueries({ queryKey: ["wa-status"] });
    } catch (e) { toast.error(apiError(e)); setConnectRequested(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Unlink your WhatsApp from Nexfy?")) return;
    try {
      await api.post("/whatsapp/logout");
      setConnectRequested(false);
      qc.invalidateQueries({ queryKey: ["wa-status"] });
      toast.success("WhatsApp unlinked");
    } catch (e) { toast.error(apiError(e)); }
  };

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 14 * 1024 * 1024) { toast.error("Max file size 14MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setAttachment({ name: file.name, type: file.type, data: reader.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const send = async () => {
    if ((!text.trim() && !attachment) || !activeJid || sending) return;
    setSending(true);
    try {
      await api.post("/whatsapp/send", { jid: activeJid, text: text.trim(), attachment });
      setText("");
      setAttachment(null);
      qc.invalidateQueries({ queryKey: ["wa-msgs", activeJid] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (!connected) {
    return (
      <div className="space-y-3 py-2 text-center" data-testid="whatsapp-disconnected">
        {status?.qr ? (
          <>
            <img src={status.qr} alt="WhatsApp QR" data-testid="whatsapp-qr-image"
              className="mx-auto h-44 w-44 rounded-xl bg-white p-1.5 shadow" />
            <p className="text-xs leading-relaxed opacity-70">
              WhatsApp → <b>Settings → Linked Devices → Link a Device</b><br />and scan this code ✦
            </p>
          </>
        ) : connectRequested || status?.connecting ? (
          <div className="py-6">
            <div className="nebula-loader mx-auto" />
            <p className="mt-3 text-xs opacity-60">Generating QR code...</p>
          </div>
        ) : (
          <>
            <WhatsAppIcon className="mx-auto h-10 w-10 text-green-600" />
            <p className="text-sm opacity-70">Link your WhatsApp and chat right from your board</p>
            <button data-testid="whatsapp-connect-button" onClick={connect}
              className="mx-auto flex items-center gap-1.5 rounded-xl bg-green-600 px-4 py-2 text-sm font-bold text-white shadow transition-transform hover:scale-105 active:scale-95">
              <QrCode className="h-4 w-4" /> Connect WhatsApp
            </button>
          </>
        )}
      </div>
    );
  }

  const otherUnread = chats.reduce((n, c) => n + (c.jid !== activeJid ? c.unread || 0 : 0), 0);

  return (
    <div className="space-y-2" data-testid="whatsapp-connected">
      <div className="flex items-center gap-1.5">
        <select data-testid="whatsapp-chat-select" value={activeJid}
          onChange={(e) => { if (e.target.value === "__new") { setNewChatOpen(true); } else setJid(e.target.value); }}
          className="min-w-0 flex-1 rounded-lg bg-black/10 dark:bg-white/10 px-2 py-1.5 text-xs outline-none cursor-pointer">
          {chats.length === 0 && !pendingChat && <option value="">Waiting for chats...</option>}
          {pendingChat && !chats.some((c) => c.jid === pendingChat.jid) && (
            <option value={pendingChat.jid}>{pendingChat.name}</option>
          )}
          {chats.map((c) => (
            <option key={c.jid} value={c.jid}>{`${c.isGroup ? "👥 " : ""}${c.name}${c.unread && c.jid !== activeJid ? ` — ${c.unread} new ●` : ""}`}</option>
          ))}
          <option value="__new">➕ New chat...</option>
        </select>
        {otherUnread > 0 && (
          <span data-testid="whatsapp-unread-badge" title={`${otherUnread} unread in other chats`}
            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-green-600 px-1.5 text-[10px] font-bold text-white">
            {otherUnread}
          </span>
        )}
        <button data-testid="whatsapp-logout-button" onClick={disconnect} title="Unlink WhatsApp"
          className="rounded-lg bg-black/10 dark:bg-white/10 p-1.5 opacity-60 transition-opacity hover:opacity-100 hover:text-red-500">
          <Unlink className="h-3.5 w-3.5" />
        </button>
      </div>

      {newChatOpen && (
        <div className="space-y-1.5 rounded-xl bg-black/10 dark:bg-white/10 p-2" data-testid="whatsapp-new-chat-panel">
          <input
            data-testid="whatsapp-new-chat-input"
            list="wa-contacts-list"
            value={newChatInput}
            onChange={(e) => setNewChatInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && startChat()}
            placeholder="Contact name or number (with country code)"
            className="w-full rounded-lg bg-white/60 dark:bg-black/30 px-2 py-1.5 text-xs outline-none placeholder:opacity-50"
            autoFocus
          />
          <datalist id="wa-contacts-list">
            {contacts.map((c) => <option key={c.jid} value={c.name} />)}
          </datalist>
          <div className="flex gap-1.5">
            <button data-testid="whatsapp-start-chat-button" onClick={startChat}
              className="flex-1 rounded-lg bg-green-600 py-1.5 text-xs font-bold text-white">Start chat ✦</button>
            <button data-testid="whatsapp-new-chat-cancel" onClick={() => { setNewChatOpen(false); setNewChatInput(""); }}
              className="rounded-lg bg-black/10 dark:bg-white/10 px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-1.5 overflow-y-auto rounded-xl bg-white/40 dark:bg-black/25 p-2" data-testid="whatsapp-messages"
        style={{ height: card?.h ? Math.max(140, card.h - 160) : 208 }}>
        {messages.length === 0 && (
          <p className="pt-14 text-center font-hand text-xl opacity-50">
            {pendingChat && activeJid === pendingChat.jid ? `say hi to ${pendingChat.name} ✦` : chats.length === 0 ? "syncing your chats... new messages appear here" : "no recent messages in this chat"}
          </p>
        )}
        {messages.map((m) => {
          const showMedia = m.mediaType && m.mediaType !== "document";
          const isLabel = MEDIA_LABELS.includes(m.text);
          return (
            <div key={m.id} className={`flex ${m.fromMe ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-sm shadow-sm ${
                m.fromMe ? "bg-green-600 text-white rounded-br-md" : "bg-white dark:bg-slate-700 dark:text-slate-100 rounded-bl-md"
              }`}>
                {!m.fromMe && m.sender && <div className="text-[10px] font-bold opacity-60">{m.sender}</div>}
                {showMedia && <WAMedia jid={activeJid} m={m} />}
                {(!showMedia || !isLabel) && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {attachment && (
        <div className="flex items-center gap-1.5 rounded-lg bg-black/10 dark:bg-white/10 px-2 py-1 text-xs" data-testid="whatsapp-attachment-chip">
          <Paperclip className="h-3 w-3" /> <span className="truncate">{attachment.name}</span>
          <button onClick={() => setAttachment(null)} className="ml-auto font-bold hover:text-red-500" data-testid="whatsapp-attachment-remove">✕</button>
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <input ref={fileRef} type="file" hidden onChange={pickFile} data-testid="whatsapp-file-input" />
        <button data-testid="whatsapp-attach-button" onClick={() => fileRef.current?.click()} title="Attach photo or file"
          className="rounded-xl bg-black/10 dark:bg-white/10 p-2 transition-colors hover:bg-black/20">
          <Paperclip className="h-4 w-4" />
        </button>
        <input data-testid="whatsapp-message-input" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message on WhatsApp..."
          className="min-w-0 flex-1 rounded-xl bg-white/60 dark:bg-black/30 px-3 py-2 text-sm outline-none placeholder:opacity-50" />
        <button data-testid="whatsapp-send-button" onClick={send} disabled={sending}
          className="rounded-xl bg-green-600 p-2 text-white shadow transition-transform hover:scale-105 active:scale-95 disabled:opacity-50">
          <Send className={`h-4 w-4 ${sending ? "animate-pulse" : ""}`} />
        </button>
      </div>
    </div>
  );
}
